import type React from 'react';
import {
    LuBold,
    LuHighlighter,
    LuImage,
    LuItalic,
    LuLink,
    LuList,
    LuListOrdered,
    LuMinus,
    LuPaintBucket,
    LuPalette,
    LuPlus,
    LuRedo2,
    LuSquare,
    LuUnderline,
    LuUndo2,
    LuX
} from 'react-icons/lu';
import { useIntl } from 'react-intl';

import { AlignMenu } from './align-menu.component';
import type { HtmlEditorMode, VisualEditorCellEdge } from './editor-types';
import { CELL_BORDER_EDGES, HTML_EDITOR_FONT_FAMILIES, HTML_EDITOR_FONT_SIZES, HTML_EDITOR_HEADINGS, htmlEditorFontSizeOptions } from './editor-types';
import { EmailColorsMenu } from './email-colors-menu.component';
import { useFormattingToolbar } from './formatting-toolbar.controller';
import { InsertTableMenu } from './insert-table-menu.component';

export interface FormattingToolbarProps {
    mode: HtmlEditorMode;
    sourceActions?: React.ReactNode;
    enableEmailRegions?: boolean;
}

const toolbarRowClassName = 'outline-border-default flex w-full items-center self-stretch px-2 py-1 outline-1 outline-offset-[-1px]';

const toolbarContentClassName = 'flex max-w-full flex-wrap items-center justify-start gap-1.5';

/** Icon buttons sit inside a group — no individual border so the bar stays dense. */
const buttonStyle =
    'flex h-8 w-8 shrink-0 items-center justify-center rounded-sm hover:bg-brand-light hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-40';

const pressedButtonStyle = `${buttonStyle} bg-brand-light`;

const iconClassName = 'size-3.5';

const toggleButtonClassName = (pressed: boolean) => (pressed ? pressedButtonStyle : buttonStyle);

/** Related controls share one outline so sections read clearly without spacing every control. */
const ToolbarGroup = (props: { children: React.ReactNode }) => (
    <div className='outline-border-default bg-background-surface inline-flex shrink-0 items-center gap-px rounded-md p-px outline-1 outline-offset-[-1px]'>
        {props.children}
    </div>
);

interface ToolbarNativeSelectOption {
    value: string;
    label: string;
}

interface ToolbarNativeSelectProps {
    id: string;
    label: string;
    value: string;
    widthClass: string;
    options: ToolbarNativeSelectOption[];
    onChange: (_value: string) => void;
    paddingClass?: string;
}

const ToolbarNativeSelect = (props: ToolbarNativeSelectProps) => (
    <select
        id={props.id}
        aria-label={props.label}
        title={props.label}
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        onMouseDown={(event) => event.stopPropagation()}
        className={`bg-background-surface text-foreground-default h-8 ${props.widthClass} shrink-0 cursor-pointer appearance-none rounded-sm border-0 ${props.paddingClass ?? 'px-1.5'} hover:bg-brand-light text-sm font-medium outline-none`}>
        {props.options.map((option) => (
            <option
                key={option.value}
                value={option.value}>
                {option.label}
            </option>
        ))}
    </select>
);

export const FormattingToolbar = (props: FormattingToolbarProps) => {
    const viewProps = useFormattingToolbar();
    const intl = useIntl();

    if (props.mode !== 'visual') {
        if (!props.sourceActions) return null;
        return <div className={toolbarRowClassName}>{props.sourceActions}</div>;
    }

    const labels = {
        bold: intl.formatMessage({
            id: 'content-detail.editor.bold',
            description: 'Toolbar button label for applying bold styling in the notification content editor',
            defaultMessage: 'Bold'
        }),
        italic: intl.formatMessage({
            id: 'content-detail.editor.italic',
            description: 'Toolbar button label for applying italic styling in the notification content editor',
            defaultMessage: 'Italic'
        }),
        underline: intl.formatMessage({
            id: 'content-detail.editor.underline',
            description: 'Toolbar button label for applying underline styling in the notification content editor',
            defaultMessage: 'Underline'
        }),
        link: intl.formatMessage({
            id: 'content-detail.editor.link',
            description: 'Toolbar button label for inserting a link in the notification content editor',
            defaultMessage: 'Link'
        }),
        bulletList: intl.formatMessage({
            id: 'content-detail.editor.bullet-list',
            description: 'Toolbar button label for applying a bullet list in the notification content editor',
            defaultMessage: 'Bullet list'
        }),
        orderedList: intl.formatMessage({
            id: 'content-detail.editor.ordered-list',
            description: 'Toolbar button label for applying a numbered list in the notification content editor',
            defaultMessage: 'Numbered list'
        }),
        divider: intl.formatMessage({
            id: 'content-detail.editor.divider',
            description: 'Toolbar button label for inserting a divider in the notification content editor',
            defaultMessage: 'Divider'
        }),
        image: intl.formatMessage({
            id: 'content-detail.editor.image',
            description: 'Toolbar button label for inserting an image in the notification content editor',
            defaultMessage: 'Image'
        }),
        textColor: intl.formatMessage({
            id: 'content-detail.editor.text-color',
            description: 'Toolbar button label for changing selected notification content text color',
            defaultMessage: 'Text color'
        }),
        highlightColor: intl.formatMessage({
            id: 'content-detail.editor.highlight-color',
            description: 'Toolbar button label for highlighting selected notification content text',
            defaultMessage: 'Highlight color'
        }),
        insertTable: intl.formatMessage({
            id: 'content-detail.editor.insert-table',
            description: 'Toolbar button label for inserting a table in the notification content visual editor',
            defaultMessage: 'Insert table'
        }),
        insertTableSize: (rows: number, cols: number) =>
            intl.formatMessage(
                {
                    id: 'content-detail.editor.insert-table-size',
                    description: 'Live size label in the HTML template editor insert-table hover grid',
                    defaultMessage: '{rows} × {cols}'
                },
                { rows, cols }
            ),
        undo: intl.formatMessage({
            id: 'content-detail.editor.undo',
            description: 'Toolbar button label for undoing the last change in the notification content visual editor',
            defaultMessage: 'Undo'
        }),
        redo: intl.formatMessage({
            id: 'content-detail.editor.redo',
            description: 'Toolbar button label for redoing the last undone change in the notification content visual editor',
            defaultMessage: 'Redo'
        }),
        align: intl.formatMessage({
            id: 'content-detail.editor.align',
            description: 'Toolbar select label for changing selected notification content text alignment',
            defaultMessage: 'Align'
        }),
        alignLeft: intl.formatMessage({
            id: 'content-detail.editor.align-left',
            description: 'Toolbar button label for left-aligning selected notification content text',
            defaultMessage: 'Align left'
        }),
        alignCenter: intl.formatMessage({
            id: 'content-detail.editor.align-center',
            description: 'Toolbar button label for center-aligning selected notification content text',
            defaultMessage: 'Align center'
        }),
        alignRight: intl.formatMessage({
            id: 'content-detail.editor.align-right',
            description: 'Toolbar button label for right-aligning selected notification content text',
            defaultMessage: 'Align right'
        }),
        alignJustify: intl.formatMessage({
            id: 'content-detail.editor.align-justify',
            description: 'Toolbar button label for justifying selected notification content text',
            defaultMessage: 'Justify'
        }),
        fontSize: intl.formatMessage({
            id: 'content-detail.editor.font-size',
            description: 'Toolbar select label for changing selected notification content font size',
            defaultMessage: 'Font size'
        }),
        decreaseFontSize: intl.formatMessage({
            id: 'content-detail.editor.decrease-font-size',
            description: 'Toolbar button label for decreasing selected notification content font size',
            defaultMessage: 'Decrease font size'
        }),
        increaseFontSize: intl.formatMessage({
            id: 'content-detail.editor.increase-font-size',
            description: 'Toolbar button label for increasing selected notification content font size',
            defaultMessage: 'Increase font size'
        }),
        fontFamily: intl.formatMessage({
            id: 'content-detail.editor.font-family',
            description: 'Toolbar select label for changing selected notification content font family',
            defaultMessage: 'Font'
        }),
        heading: intl.formatMessage({
            id: 'html-template-editor.editor.formatting-toolbar.heading',
            description: 'Toolbar select label for changing the block style of selected notification content',
            defaultMessage: 'Style'
        }),
        headingParagraph: intl.formatMessage({
            id: 'html-template-editor.editor.formatting-toolbar.heading-paragraph',
            description: 'Toolbar option that applies a paragraph block in the HTML template editor',
            defaultMessage: 'Paragraph'
        }),
        headingLevel: (level: number) =>
            intl.formatMessage(
                {
                    id: 'html-template-editor.editor.formatting-toolbar.heading-level',
                    description: 'Toolbar option that applies a heading level in the HTML template editor',
                    defaultMessage: 'Heading {level}'
                },
                { level }
            ),
        cellBorderPicker: intl.formatMessage({
            id: 'content-detail.editor.cell-border-picker',
            description: 'Accessible label for the table cell border edge picker in the HTML template editor',
            defaultMessage: 'Cell borders'
        }),
        cellBorderTop: intl.formatMessage({
            id: 'content-detail.editor.cell-border-top',
            description: 'Toolbar control that selects the top edge of the current table cell border',
            defaultMessage: 'Top border'
        }),
        cellBorderRight: intl.formatMessage({
            id: 'content-detail.editor.cell-border-right',
            description: 'Toolbar control that selects the right edge of the current table cell border',
            defaultMessage: 'Right border'
        }),
        cellBorderBottom: intl.formatMessage({
            id: 'content-detail.editor.cell-border-bottom',
            description: 'Toolbar control that selects the bottom edge of the current table cell border',
            defaultMessage: 'Bottom border'
        }),
        cellBorderLeft: intl.formatMessage({
            id: 'content-detail.editor.cell-border-left',
            description: 'Toolbar control that selects the left edge of the current table cell border',
            defaultMessage: 'Left border'
        }),
        cellBorderAll: intl.formatMessage({
            id: 'content-detail.editor.cell-border-all',
            description: 'Toolbar control that selects every edge of the current table cell border',
            defaultMessage: 'All borders'
        }),
        cellBorderClear: intl.formatMessage({
            id: 'content-detail.editor.cell-border-clear',
            description: 'Toolbar button that removes the selected table cell border edges',
            defaultMessage: 'Clear cell borders'
        }),
        cellBorderColor: intl.formatMessage({
            id: 'content-detail.editor.cell-border-color',
            description: 'Toolbar color control that paints the selected table cell border edges',
            defaultMessage: 'Cell border color'
        }),
        cellFill: intl.formatMessage({
            id: 'content-detail.editor.cell-fill',
            description: 'Toolbar color control that sets the current table cell background fill',
            defaultMessage: 'Cell fill'
        }),
        cellFillClear: intl.formatMessage({
            id: 'content-detail.editor.cell-fill-clear',
            description: 'Toolbar button that removes the current table cell background fill',
            defaultMessage: 'Clear cell fill'
        }),
        emailColors: intl.formatMessage({
            id: 'html-template-editor.editor.formatting-toolbar.email-colors',
            description: 'Toolbar button that opens email page, header, and card background color controls',
            defaultMessage: 'Email colors'
        }),
        emailColorsMenu: intl.formatMessage({
            id: 'html-template-editor.editor.formatting-toolbar.email-colors-menu',
            description: 'Accessible label for the email background colors popover in the HTML template editor',
            defaultMessage: 'Email background colors'
        }),
        emailCanvasColor: intl.formatMessage({
            id: 'html-template-editor.editor.formatting-toolbar.email-canvas-color',
            description: 'Toolbar color control that sets the email page canvas background',
            defaultMessage: 'Page background'
        }),
        emailHeaderColor: intl.formatMessage({
            id: 'html-template-editor.editor.formatting-toolbar.email-header-color',
            description: 'Toolbar color control that sets the email header band background',
            defaultMessage: 'Header background'
        }),
        emailCardColor: intl.formatMessage({
            id: 'html-template-editor.editor.formatting-toolbar.email-card-color',
            description: 'Toolbar color control that sets the inner email card background',
            defaultMessage: 'Card background'
        })
    };

    return (
        <div className={toolbarRowClassName}>
            <div className={toolbarContentClassName}>
                <ToolbarGroup>
                    <button
                        id='undo-button'
                        type='button'
                        aria-label={labels.undo}
                        title={labels.undo}
                        onClick={() => viewProps.dispatchCommand({ type: 'undo' })}
                        className={buttonStyle}>
                        <LuUndo2 className={iconClassName} />
                    </button>
                    <button
                        id='redo-button'
                        type='button'
                        aria-label={labels.redo}
                        title={labels.redo}
                        onClick={() => viewProps.dispatchCommand({ type: 'redo' })}
                        className={buttonStyle}>
                        <LuRedo2 className={iconClassName} />
                    </button>
                </ToolbarGroup>

                <ToolbarGroup>
                    <ToolbarNativeSelect
                        id='heading-select'
                        label={labels.heading}
                        value={viewProps.heading ?? 'text'}
                        widthClass='w-28'
                        options={HTML_EDITOR_HEADINGS.map((heading) => ({
                            value: heading,
                            label: heading === 'text' ? labels.headingParagraph : labels.headingLevel(Number(heading.slice(1)))
                        }))}
                        onChange={(value) => viewProps.changeHeading(value)}
                    />
                    <ToolbarNativeSelect
                        id='font-family-select'
                        label={labels.fontFamily}
                        value={viewProps.fontFamily}
                        widthClass='w-28'
                        options={HTML_EDITOR_FONT_FAMILIES.map((family) => ({ value: family.value, label: family.label }))}
                        onChange={(value) => viewProps.changeFontFamily(value)}
                    />
                    <button
                        id='decrease-font-size-button'
                        type='button'
                        aria-label={labels.decreaseFontSize}
                        title={labels.decreaseFontSize}
                        disabled={Number(viewProps.fontSize) <= Number(HTML_EDITOR_FONT_SIZES[0])}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => viewProps.stepFontSize(-1)}
                        className={buttonStyle}>
                        <LuMinus className={iconClassName} />
                    </button>
                    <ToolbarNativeSelect
                        id='font-size-select'
                        label={labels.fontSize}
                        value={viewProps.fontSize}
                        widthClass='w-14'
                        paddingClass='px-1'
                        options={htmlEditorFontSizeOptions(viewProps.fontSize).map((size) => ({ value: size, label: size }))}
                        onChange={(value) => viewProps.changeFontSize(value)}
                    />
                    <button
                        id='increase-font-size-button'
                        type='button'
                        aria-label={labels.increaseFontSize}
                        title={labels.increaseFontSize}
                        disabled={Number(viewProps.fontSize) >= Number(HTML_EDITOR_FONT_SIZES[HTML_EDITOR_FONT_SIZES.length - 1])}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => viewProps.stepFontSize(1)}
                        className={buttonStyle}>
                        <LuPlus className={iconClassName} />
                    </button>
                </ToolbarGroup>

                <ToolbarGroup>
                    <button
                        id='bold-button'
                        type='button'
                        aria-label={labels.bold}
                        title={labels.bold}
                        aria-pressed={viewProps.bold}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => viewProps.dispatchCommand({ type: 'bold' })}
                        className={toggleButtonClassName(viewProps.bold)}>
                        <LuBold className={iconClassName} />
                    </button>
                    <button
                        id='italic-button'
                        type='button'
                        aria-label={labels.italic}
                        title={labels.italic}
                        aria-pressed={viewProps.italic}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => viewProps.dispatchCommand({ type: 'italic' })}
                        className={toggleButtonClassName(viewProps.italic)}>
                        <LuItalic className={iconClassName} />
                    </button>
                    <button
                        id='underline-button'
                        type='button'
                        aria-label={labels.underline}
                        title={labels.underline}
                        aria-pressed={viewProps.underline}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => viewProps.dispatchCommand({ type: 'underline' })}
                        className={toggleButtonClassName(viewProps.underline)}>
                        <LuUnderline className={iconClassName} />
                    </button>
                    <label
                        id='text-color-button'
                        aria-label={labels.textColor}
                        title={labels.textColor}
                        className={`relative ${buttonStyle}`}>
                        <LuPalette className={iconClassName} />
                        <span
                            className='absolute bottom-1 h-1 w-4 rounded-full'
                            style={{ backgroundColor: viewProps.textColor }}
                        />
                        <input
                            type='color'
                            aria-label={labels.textColor}
                            value={viewProps.textColor}
                            onChange={(event) => viewProps.changeTextColor(event.target.value)}
                            className='absolute inset-0 cursor-pointer opacity-0'
                        />
                    </label>
                    <label
                        id='highlight-color-button'
                        aria-label={labels.highlightColor}
                        title={labels.highlightColor}
                        className={`relative ${buttonStyle}`}>
                        <LuHighlighter className={iconClassName} />
                        <span
                            className='absolute bottom-1 h-1 w-4 rounded-full'
                            style={{ backgroundColor: viewProps.highlightColor }}
                        />
                        <input
                            type='color'
                            aria-label={labels.highlightColor}
                            value={viewProps.highlightColor}
                            onChange={(event) => viewProps.changeHighlightColor(event.target.value)}
                            className='absolute inset-0 cursor-pointer opacity-0'
                        />
                    </label>
                </ToolbarGroup>

                <ToolbarGroup>
                    <button
                        id='link-button'
                        type='button'
                        aria-label={labels.link}
                        title={labels.link}
                        onClick={() => viewProps.setLink()}
                        className={buttonStyle}>
                        <LuLink className={iconClassName} />
                    </button>
                </ToolbarGroup>

                {props.enableEmailRegions ? (
                    <ToolbarGroup>
                        <EmailColorsMenu
                            buttonClassName={buttonStyle}
                            labels={{
                                trigger: labels.emailColors,
                                menu: labels.emailColorsMenu,
                                canvas: labels.emailCanvasColor,
                                header: labels.emailHeaderColor,
                                card: labels.emailCardColor
                            }}
                            canvasColor={viewProps.emailRegionColors.canvas}
                            headerColor={viewProps.emailRegionColors.header}
                            cardColor={viewProps.emailRegionColors.card}
                            onCanvasChange={(color) => viewProps.changeEmailRegionColor('canvas', color)}
                            onHeaderChange={(color) => viewProps.changeEmailRegionColor('header', color)}
                            onCardChange={(color) => viewProps.changeEmailRegionColor('card', color)}
                        />
                    </ToolbarGroup>
                ) : null}

                <ToolbarGroup>
                    <AlignMenu
                        value={viewProps.align}
                        onChange={viewProps.changeAlign}
                        buttonClassName={buttonStyle}
                        labels={{
                            menu: labels.align,
                            left: labels.alignLeft,
                            center: labels.alignCenter,
                            right: labels.alignRight,
                            justify: labels.alignJustify
                        }}
                    />
                    <button
                        id='bullet-list-button'
                        type='button'
                        aria-label={labels.bulletList}
                        title={labels.bulletList}
                        onClick={() => viewProps.dispatchCommand({ type: 'insertUnorderedList' })}
                        className={buttonStyle}>
                        <LuList className={iconClassName} />
                    </button>
                    <button
                        id='ordered-list-button'
                        type='button'
                        aria-label={labels.orderedList}
                        title={labels.orderedList}
                        onClick={() => viewProps.dispatchCommand({ type: 'insertOrderedList' })}
                        className={buttonStyle}>
                        <LuListOrdered className={iconClassName} />
                    </button>
                </ToolbarGroup>

                {viewProps.inTable ? (
                    <ToolbarGroup>
                        <CellEdgePicker
                            edges={viewProps.borderEdges}
                            labels={{
                                picker: labels.cellBorderPicker,
                                top: labels.cellBorderTop,
                                right: labels.cellBorderRight,
                                bottom: labels.cellBorderBottom,
                                left: labels.cellBorderLeft,
                                all: labels.cellBorderAll
                            }}
                            onToggle={viewProps.toggleBorderEdge}
                            onToggleAll={viewProps.toggleAllBorderEdges}
                        />
                        <button
                            id='clear-cell-borders-button'
                            type='button'
                            aria-label={labels.cellBorderClear}
                            title={labels.cellBorderClear}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={viewProps.clearCellBorders}
                            className={buttonStyle}>
                            <LuX className={iconClassName} />
                        </button>
                        <label
                            id='cell-border-color-button'
                            aria-label={labels.cellBorderColor}
                            title={labels.cellBorderColor}
                            className={`relative ${buttonStyle}`}>
                            <LuSquare className={iconClassName} />
                            <span
                                className='absolute bottom-1 h-1 w-4 rounded-full'
                                style={{ backgroundColor: viewProps.borderColor }}
                            />
                            <input
                                id='cell-border-color-input'
                                type='color'
                                aria-label={labels.cellBorderColor}
                                value={viewProps.borderColor}
                                onChange={(event) => viewProps.changeBorderColor(event.target.value)}
                                className='absolute inset-0 cursor-pointer opacity-0'
                            />
                        </label>
                        <label
                            id='cell-fill-button'
                            aria-label={labels.cellFill}
                            title={labels.cellFill}
                            className={`relative ${buttonStyle}`}>
                            <LuPaintBucket className={iconClassName} />
                            <span
                                className='absolute bottom-1 h-1 w-4 rounded-full'
                                style={{ backgroundColor: viewProps.cellFillColor }}
                            />
                            <input
                                id='cell-fill-color-input'
                                type='color'
                                aria-label={labels.cellFill}
                                value={viewProps.cellFillColor}
                                onChange={(event) => viewProps.changeCellFillColor(event.target.value)}
                                className='absolute inset-0 cursor-pointer opacity-0'
                            />
                        </label>
                        <button
                            id='clear-cell-fill-button'
                            type='button'
                            aria-label={labels.cellFillClear}
                            title={labels.cellFillClear}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={viewProps.clearCellFill}
                            className={buttonStyle}>
                            <LuX className={iconClassName} />
                        </button>
                    </ToolbarGroup>
                ) : null}

                <ToolbarGroup>
                    <button
                        id='image-button'
                        type='button'
                        aria-label={labels.image}
                        title={labels.image}
                        onClick={viewProps.insertImage}
                        className={buttonStyle}>
                        <LuImage className={iconClassName} />
                    </button>
                    <InsertTableMenu
                        buttonClassName={buttonStyle}
                        labels={{
                            trigger: labels.insertTable,
                            menu: labels.insertTable,
                            size: labels.insertTableSize
                        }}
                        onSelect={(size) => viewProps.dispatchCommand({ type: 'insertTable', rows: size.rows, cols: size.cols })}
                    />
                    <button
                        id='divider-button'
                        type='button'
                        aria-label={labels.divider}
                        title={labels.divider}
                        onClick={() => viewProps.dispatchCommand({ type: 'insertHorizontalRule' })}
                        className={buttonStyle}>
                        <LuMinus className={iconClassName} />
                    </button>
                </ToolbarGroup>
            </div>
        </div>
    );
};

const CellEdgePicker = (props: {
    edges: VisualEditorCellEdge[];
    labels: { picker: string; top: string; right: string; bottom: string; left: string; all: string };
    onToggle: (_edge: VisualEditorCellEdge) => void;
    onToggleAll: () => void;
}) => {
    const selected = (edge: VisualEditorCellEdge) => props.edges.includes(edge);
    const edgeClass = (edge: VisualEditorCellEdge) =>
        `absolute rounded-sm hover:cursor-pointer ${selected(edge) ? 'bg-brand-primary' : 'bg-text-secondary/30 hover:bg-text-secondary/60'}`;

    return (
        <div
            role='group'
            aria-label={props.labels.picker}
            className='relative h-8 w-8 shrink-0'>
            <button
                type='button'
                aria-label={props.labels.top}
                aria-pressed={selected('top')}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => props.onToggle('top')}
                className={`${edgeClass('top')} top-0 right-1 left-1 h-1`}
            />
            <button
                type='button'
                aria-label={props.labels.right}
                aria-pressed={selected('right')}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => props.onToggle('right')}
                className={`${edgeClass('right')} top-1 right-0 bottom-1 w-1`}
            />
            <button
                type='button'
                aria-label={props.labels.bottom}
                aria-pressed={selected('bottom')}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => props.onToggle('bottom')}
                className={`${edgeClass('bottom')} right-1 bottom-0 left-1 h-1`}
            />
            <button
                type='button'
                aria-label={props.labels.left}
                aria-pressed={selected('left')}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => props.onToggle('left')}
                className={`${edgeClass('left')} top-1 bottom-1 left-0 w-1`}
            />
            <button
                type='button'
                aria-label={props.labels.all}
                aria-pressed={props.edges.length === CELL_BORDER_EDGES.length}
                onMouseDown={(event) => event.preventDefault()}
                onClick={props.onToggleAll}
                className='outline-border-default hover:outline-brand-primary absolute inset-1.5 rounded-sm outline-1 outline-offset-[-1px] hover:cursor-pointer'
            />
        </div>
    );
};
